<template>
  <div class="login-form-container" style="width: 100%; min-width: 100%;">
    <!-- Login Type Selector -->
    <div class="login-type-selector">
      <div class="selector-container">

        <button
          ref="adminButton"
          @click="selectLoginType('admin')"
          :class="['selector-option', { active: loginType === 'admin' }]"
          type="button"
        >
          <i class="material-icons">admin_panel_settings</i>
          <span>ADMIN</span>
        </button>

        <button
          ref="employeeButton"
          @click="selectLoginType('employee')"
          :class="['selector-option', { active: loginType === 'employee' }]"
          type="button"
        >
          <i class="material-icons">person</i>
          <span>EMPLOYEE</span>
        </button>

        <button
          ref="partnerButton"
          @click="selectLoginType('partner')"
          :class="['selector-option', { active: loginType === 'partner' }]"
          type="button"
        >
          <i class="material-icons">business</i>
          <span>PARTNER</span>
        </button>
      </div>
    </div>

    <!-- Title -->
    <h3 v-if="mode === 'forgot'" class="card-title">Reset Password</h3>
    <h3 v-else-if="loginType === 'partner'" class="card-title">Partner Portal</h3>
    <h3 v-else-if="loginType === 'admin'" class="card-title">Admin Portal</h3>
    <h3 v-else class="card-title">Employee Portal</h3>

    <!-- Login Form -->
    <form :action="formAction" method="post" class="login-form">
      <input type="hidden" name="authenticity_token" :value="csrfToken" />

      <div class="form-inputs-wrapper">
        <div :key="`${loginType}-${mode}`" class="form-inputs">
          <!-- Forgot Password Mode -->
          <template v-if="mode === 'forgot'">
            <!-- Employee forgot password fields -->
            <template v-if="loginType === 'employee'">
              <div class="input-field">
                <input
                  type="text"
                  :name="employeeFieldPrefix + '[organization_login]'"
                  placeholder="Organisation Login"
                  v-model="formData.organizationLogin"
                  maxlength="50"
                />
              </div>

              <div class="input-field">
                <input
                  type="email"
                  :name="employeeFieldPrefix + '[email]'"
                  placeholder="Email Address"
                  v-model="formData.email"
                  maxlength="50"
                />
              </div>
            </template>

            <!-- Admin/Partner forgot password fields -->
            <template v-else>
              <div class="input-field">
                <input
                  type="email"
                  :name="loginType === 'partner' ? 'agent[email]' : 'admin[email]'"
                  placeholder="Email"
                  v-model="formData.email"
                  maxlength="50"
                />
              </div>
            </template>
          </template>

          <!-- Login Mode -->
          <template v-else>
            <!-- Admin/Partner Form Fields -->
            <div v-if="loginType === 'admin' || loginType === 'partner'" class="input-field">
              <input
                type="email"
                :name="loginType === 'partner' ? 'agent[email]' : 'admin[email]'"
                :placeholder="loginType === 'partner' ? 'Partner Email' : 'Administrator Email'"
                v-model="formData.email"
                maxlength="50"
              />
            </div>

            <!-- Employee Form Fields -->
            <template v-if="loginType === 'employee'">
              <div class="input-field">
                <input
                  type="text"
                  :name="employeeFieldPrefix + '[organization_login]'"
                  placeholder="Organisation Login ID"
                  v-model="formData.organizationLogin"
                  maxlength="50"
                />
              </div>

              <div class="input-field">
                <input
                  type="text"
                  :name="employeeFieldPrefix + '[login]'"
                  placeholder="Username / Email / ID"
                  v-model="formData.login"
                  maxlength="50"
                />
              </div>
            </template>

            <!-- Password Field (all types) -->
            <div class="input-field">
              <span class="password-visibility">
                <input
                  :type="showPassword ? 'text' : 'password'"
                  :name="passwordFieldName"
                  placeholder="Password"
                  autocomplete="current-password"
                  v-model="formData.password"
                  id="password"
                />
                <password-visibility-button
                  :visible="showPassword"
                  field-label="password"
                  @toggle="togglePassword"
                />
              </span>
            </div>
          </template>
        </div>
      </div>

      <div
        :id="
          loginType === 'partner'
            ? 'new-submit-partners'
            : loginType === 'employee'
            ? 'new-submit-employee'
            : 'new-submit-normal'
        "
      >
        <button type="submit" class="btn">{{ mode === 'forgot' ? 'Reset my password' : 'Log in' }}</button>
      </div>

      <div class="form-links">
        <a v-if="mode === 'login'" @click.prevent="toggleMode" href="#" style="cursor: pointer;">Forgot password?</a>
        <a v-else @click.prevent="toggleMode" href="#" style="cursor: pointer;">Back to login</a>
      </div>
    </form>
  </div>
</template>

<script>
import PasswordVisibilityButton from './password_visibility_button.vue';

export default {
  name: 'LoginForm',
  components: { PasswordVisibilityButton },
  props: {
    initialType: {
      type: String,
      default: 'admin',
      validator: (value) => ['admin', 'employee', 'partner'].includes(value),
    },
    csrfToken: {
      type: String,
      required: true,
    },
    adminLoginUrl: {
      type: String,
      default: '/login',
    },
    employeeLoginUrl: {
      type: String,
      default: '/staff/login',
    },
    partnerLoginUrl: {
      type: String,
      default: '/login',
    },
    adminForgotPasswordUrl: {
      type: String,
      default: '/password/new',
    },
    employeeForgotPasswordUrl: {
      type: String,
      default: '/staff/password/new',
    },
    partnerForgotPasswordUrl: {
      type: String,
      default: '/password/new',
    },
    adminForgotPasswordPageUrl: {
      type: String,
      default: '/password/new',
    },
    employeeForgotPasswordPageUrl: {
      type: String,
      default: '/staff/password/new',
    },
    partnerForgotPasswordPageUrl: {
      type: String,
      default: '/password/new',
    },
    adminRegisterUrl: {
      type: String,
      default: '/register',
    },
    partnerRegisterUrl: {
      type: String,
      default: '/register',
    },
    initialMode: {
      type: String,
      default: 'login',
      validator: (value) => ['login', 'forgot'].includes(value),
    },
  },
  data() {
    return {
      loginType: this.initialType,
      mode: this.initialMode || 'login', // 'login' or 'forgot'
      showPassword: false,
      formData: {
        email: '',
        organizationLogin: '',
        login: '',
        password: '',
      },
    };
  },
  computed: {
    portalConfigs() {
      return {
        admin: {
          login: this.adminLoginUrl,
          forgot: this.adminForgotPasswordUrl,
          forgotPage: this.adminForgotPasswordPageUrl,
        },
        employee: {
          login: this.employeeLoginUrl,
          forgot: this.employeeForgotPasswordUrl,
          forgotPage: this.employeeForgotPasswordPageUrl,
        },
        partner: {
          login: this.partnerLoginUrl,
          forgot: this.partnerForgotPasswordUrl,
          forgotPage: this.partnerForgotPasswordPageUrl,
        },
      };
    },
    currentPortalConfig() {
      return this.portalConfigs[this.loginType] || this.portalConfigs.admin;
    },
    formAction() {
      return this.mode === 'forgot' ? this.currentPortalConfig.forgot : this.currentPortalConfig.login;
    },
    navigationUrl() {
      return this.mode === 'forgot' ? this.currentPortalConfig.forgotPage : this.currentPortalConfig.login;
    },
    employeeFieldPrefix() {
      // If the employee login URL contains '/staff/', it's root domain (use root_employee)
      // Otherwise, it's subdomain (use employee)
      // Check both absolute and relative URLs
      const url = this.employeeLoginUrl || '';
      if (url.includes('/staff/') || url.includes('/staff/login')) {
        return 'root_employee';
      }
      // Default to 'employee' for subdomain logins
      return 'employee';
    },
    passwordFieldName() {
      const names = {
        admin: 'admin[password]',
        employee: this.employeeFieldPrefix + '[password]',
        partner: 'agent[password]',
      };
      return names[this.loginType] || 'admin[password]';
    },
  },
  methods: {
    selectLoginType(type) {
      if (this.loginType !== type) {
        // Clear form data when switching types
        this.formData = {
          email: '',
          organizationLogin: '',
          login: '',
          password: '',
        };
        this.loginType = type;
        this.showPassword = false;
        // Update body background color
        this.updateBackgroundColor(type);
        // Update auth class for CSS styling
        this.updateAuthClass(type);
      }
    },
    togglePassword() {
      this.showPassword = !this.showPassword;
    },
    toggleMode() {
      this.mode = this.mode === 'login' ? 'forgot' : 'login';
      this.showPassword = false;
      // Clear form data when switching modes
      this.formData = {
        email: '',
        organizationLogin: '',
        login: '',
        password: '',
      };
    },
    updateBackgroundColor(type) {
      // Background is now handled by CSS classes on main.auth
      // This method is kept for consistency but the actual styling is in SCSS
    },
    updateAuthClass(type) {
      const authMain = document.querySelector('main.auth');
      if (authMain) {
        // Remove all portal classes
        authMain.classList.remove('admin', 'employee', 'partners');
        // Add the current portal class
        if (type === 'partner') {
          authMain.classList.add('partners');
        } else if (type === 'employee') {
          authMain.classList.add('employee');
        } else {
          authMain.classList.add('admin');
        }
      }

      // Hide/show and update registration link based on login type
      const authLinks = document.querySelector('.auth-links');
      if (authLinks) {
        // Find the paragraph containing the registration link
        const allParagraphs = authLinks.querySelectorAll('p');
        allParagraphs.forEach((p) => {
          const link = p.querySelector('a[href*="register"]');
          if (link) {
            if (type === 'employee') {
              p.style.display = 'none';
            } else {
              p.style.display = 'block';
              // Update link text and URL based on login type
              if (type === 'partner') {
                link.textContent = 'Register new partner account';
                link.href = this.partnerRegisterUrl;
              } else {
                link.textContent = 'Register new company account';
                link.href = this.adminRegisterUrl;
              }
            }
          }
        });
      }
    },
    getHostFromUrl(url) {
      if (!url || typeof window === 'undefined') {
        return null;
      }
      try {
        return new URL(url, window.location.origin).host;
      } catch (e) {
        return null;
      }
    },
    ensureCorrectHost() {
      if (typeof window === 'undefined') {
        return false;
      }
      const destination = this.navigationUrl;
      const targetHost = this.getHostFromUrl(destination);
      if (!destination || !targetHost) {
        return false;
      }
      if (window.location.host === targetHost) {
        return false;
      }
      window.location.href = destination;
      return true;
    },
  },
  watch: {
    loginType() {
      this.$nextTick(() => {
        this.ensureCorrectHost();
      });
    },
    mode() {
      this.$nextTick(() => {
        this.ensureCorrectHost();
      });
    },
  },
  mounted() {
    if (this.ensureCorrectHost()) {
      return;
    }
    // Set initial background color
    this.updateBackgroundColor(this.loginType);
    // Set initial auth class
    this.updateAuthClass(this.loginType);

    // Update background color when component is destroyed
    this.$once('hook:beforeDestroy', () => {
      document.body.style.background = '';
      document.body.style.backgroundAttachment = '';
      const authMain = document.querySelector('main.auth');
      if (authMain) {
        authMain.classList.remove('admin', 'employee', 'partners');
      }
    });
  },
};
</script>

<style scoped lang="scss">
.login-form-container {
  width: 100%;
}
</style>
